import {useEffect, useRef} from "react";
import {useSelector} from "react-redux";
import Skeleton from "react-loading-skeleton";
import {formatDateTime} from "utils/valueFormatter.function";
import * as reduxSelectors from "components/store/application.reducers";
import {useTranslation} from "components/translations/useTranslation";
import type {Comment} from "types/comments";
import type {CommentsListProps} from "types/taskDetailsComments";
import CommentsProfile from "./CommentsProfile.component";

// the comment event types that were given with a task action, which the comment names under its author
const EVENT_TYPES_WITH_ACTION = [1, 2, 3, 5, 9, 22, 55];

const SKELETON_ROWS = 3;

/**
 * The comments of the task, oldest first, each in a card with its author, when it was written and
 * the action it was given with. The list keeps to a height of its own and opens scrolled to the latest.
 */
export const CommentsList = ({readOnly}: CommentsListProps) => {
    const {translate} = useTranslation();
    const isLoading: boolean = useSelector(reduxSelectors.getTaskDraftCommentsLoadingStatus);
    const comments: Comment[] = useSelector(reduxSelectors.getTaskDraftComments);
    const allowExtraComments: boolean = useSelector(reduxSelectors.getAllowExtraComments);
    const listRef = useRef<HTMLOListElement>(null);

    // the current task's own comment is the draft, shown in the field, unless comments can be added on their own
    const visibleComments = [...comments].reverse()
        .map((comment, index) => ({comment, index}))
        .filter(({comment}) => allowExtraComments || !comment.current);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [comments, allowExtraComments, isLoading]);

    if (isLoading) {
        return (
            <div className="comments-module__loading" data-cy="taskDetail.comments.loading">
                {Array.from({length: SKELETON_ROWS}, (_, index) =>
                    <div key={index} className="comments-module__loading-item">
                        <Skeleton className="comments-module__loading-picture"/>
                        <div className="comments-module__loading-content">
                            <Skeleton className="comments-module__loading-line comments-module__loading-line--short"/>
                            <Skeleton className="comments-module__loading-line"/>
                        </div>
                    </div>)}
            </div>
        );
    }

    if (comments.length === 0) {
        return (
            <div className="comments-module__empty">
                <p className="comments-module__empty-title">
                    {readOnly ? translate("taskDetail.noCommentsAdded") : translate("taskDetail.emptyComment")}
                </p>
                {!readOnly && <p className="comments-module__empty-info">{translate("taskDetail.emptyCommentMoreInfo")}</p>}
            </div>
        );
    }

    return (
        <ol className="comments-module__list" ref={listRef}>
            {visibleComments.map(({comment, index}) =>
                <li className="comments-module__item" key={`${comment.nodeId}-${index}`}
                    data-cy="taskDetail.comments.listitem">
                    <CommentsProfile connectUserId={comment.connectUserId}
                                     userName={comment.userName}
                                     externalApplicationName={comment.externalApplicationName}
                                     eventType={comment.eventType}/>
                    <div className="comments-module__item-body">
                        <p className="comments-module__item-head">
                            <span className="comments-module__author">{comment.userName}</span>
                            <span className="comments-module__when">{formatDateTime(comment.createdDate, "")}</span>
                        </p>
                        {EVENT_TYPES_WITH_ACTION.includes(comment.eventType) &&
                            <p className="comments-module__action">
                                {translate(`taskDetail.commentsAction.${comment.eventType}`)}
                            </p>}
                        <p className="comments-module__text" data-cy="taskDetail.comments.listitem.content">
                            {comment.content}
                        </p>
                    </div>
                </li>)}
        </ol>
    );
};

export default CommentsList;
